import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    return {
        currentPage: getCurrentPage(state),
        catalogPageState,
        breadCrumbs: state.components.breadcrumbs.links,
        brandPath,
        categoryFacets: getProductListDataViewProperty(state, "properties"),
    };
};

type Props = HasCategoryContext & OwnProps & ReturnType<typeof mapStateToProps> & ThemeProps<BaseTheme>;

export interface CENTaxonomyCategoryDescriptionStyles {
    container?: InjectableCss;
    categoryDetails?: InjectableCss;
    linkStyles?: InjectableCss;
}

export const cenTaxonomyCategoryDescriptionStyles: CENTaxonomyCategoryDescriptionStyles = {
    container: {
        css: css`
            padding-right: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-right: 0px;
                    `,
                    css`
                        padding-right: 0px;
                    `,
                    css`
                        padding-right: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    categoryDetails: {
        css: css`
            p {
                font-size: 18px !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 28px;
                font-family: "Poppins" !important;
                color: ${getProp("theme.typography.p.color")};
            }

            html body {
                font-size: 16px !important;
                font-family: "Poppins" !important;
                p {
                    margin-top: 0px;
                    margin-bottom: 0.5rem !important;
                    font-size: 18px !important;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    line-height: 28px;
                    font-family: "Poppins" !important;
                    color: ${getProp("theme.typography.p.color")};
                }
            }
        `,
    },
    linkStyles: {
        css: css`
            margin-top: 0px;
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
            &:focus {
                outline-color: transparent !important;
            }
            > span {
                color: ${getProp("theme.typography.p.color")} !important ;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
};

const styles = cenTaxonomyCategoryDescriptionStyles;

const CENTaxonomyCategoryDescription: FC<Props> = ({
    fields,
    catalogPageState,
    category,
    breadCrumbs,
    currentPage,
    brandPath,
    categoryFacets,
    theme,
}) => {
    const subCategories =
        catalogPageState?.value?.properties?.subCategories &&
        JSON.parse(decodeURI(catalogPageState?.value.properties?.subCategories));

    const categoryFacetsData = JSON.parse(categoryFacets?.categoryList ?? "[]");

    const lastLevelCategory = breadCrumbs?.[breadCrumbs?.length - 1]?.children;
    const categoryFacetsDataNew = categoryFacetsData?.filter((item: any) => item.Name !== lastLevelCategory);

    const isBrandPath = brandPath?.toLowerCase()?.includes("/brands");
    if ((isBrandPath && categoryFacetsDataNew?.length > 0) || (!isBrandPath && subCategories?.length > 0)) {
        return null;
    }
    const categoryPath = category?.path;
    const shouldDisplayBrand = categoryPath && categoryPath.toLowerCase()?.includes("/brands");
    const breadCrumbArray = breadCrumbs?.map((item: any) => item.children);
    const brandName = breadCrumbArray?.[2];

    const isSearchCrawler = getIsWebCrawler();

    const [status, setStatus] = useState(false);

    const toggleStatus = () => {
        setStatus(!status);
    };

    const contentArr = category?.htmlContent?.split("</head>") ?? [];
    const contentLength: any = contentArr?.length > 1 ? contentArr?.[1]?.length : category?.htmlContent?.length;

    const [truncateLength, setTruncateLength] = useState(220);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const handleResize = () => {
            setTruncateLength(window.innerWidth <= 768 ? 135 : 220);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <>
            <StyledWrapper {...styles.container}>
                {shouldDisplayBrand ? (
                    <Typography
                        variant="h1"
                        data-test-selector={`category_BrandName_${
                            category.shortDescription ?? category.name
                        }_${brandName}`}
                    >
                        {" "}
                        {category?.shortDescription ?? category?.name} - {brandName}
                    </Typography>
                ) : (category?.shortDescription || category?.name) && (
                        <Typography
                            variant={isSearchCrawler && currentPage.type === "CategoryDetailsPage" ? "h1" : "h1"}
                            data-test-selector={`categoryName_${category?.shortDescription ?? category?.name}`}
                        >
                            {category?.shortDescription ?? category?.name}
                        </Typography>
                    )}
                <StyledWrapper {...styles.categoryDetails}>
                    {status || getIsWebCrawler()
                        ? parse(category?.htmlContent ?? "", parserOptions)
                        : parse(truncate(category?.htmlContent, { length: truncateLength }), parserOptions)}
                    {contentLength > truncateLength && !getIsWebCrawler() && (
                        <Link href="" onClick={toggleStatus} {...styles.linkStyles}>
                            {status ? "Show Less" : "Show More"}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const CenTaxonomyCategoryDescription: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(withTheme(CENTaxonomyCategoryDescription))),
    definition: {
        group: "Categories",
        displayName: "CEN Taxonomy Description",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};
export default CenTaxonomyCategoryDescription;
